import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Copy, RefreshCw, Loader2 } from "lucide-react";

const POLL_MS = 5000;

export default function PixPayment({ initial = null, onPaid = null }) {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [pix, setPix] = useState(initial);
  const [error, setError] = useState(null);

  const create = useCallback(async () => {
    setError(null);
    setPix(null);
    try {
      const { data } = await api.post("/payments/pix");
      setPix(data);
    } catch (e) { setError(formatApiError(e)); }
  }, []);

  useEffect(() => { if (!initial) { create(); } }, [create, initial]);

  useEffect(() => {
    if (!pix || pix.status !== "PENDING") { return undefined; }
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/pix/${pix.id}`);
        setPix(data);
        if (data.status === "PAID" && onPaid) {
          onPaid();
        } else if (data.status === "PAID") {
          toast.success("Pagamento confirmado! Seu plano foi ativado.");
          await refresh();
          navigate("/dashboard");
        }
      } catch (e) { console.warn("PIX status check failed:", e); }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [pix, refresh, navigate, onPaid]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pix.payment_code);
      toast.success("Código PIX copiado!");
    } catch (e) { toast.error("Não foi possível copiar. Copie manualmente."); }
  };

  if (error) {
    return (
      <Card className="p-5 mt-6 border-rose-500/40" data-testid="pix-error">
        <p className="text-sm text-rose-500">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={initial ? onPaid : create} data-testid="pix-retry-btn">
          <RefreshCw className="h-3 w-3 mr-2" /> Tentar novamente
        </Button>
      </Card>
    );
  }
  if (!pix) {
    return (
      <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="pix-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Gerando QR Code PIX...
      </div>
    );
  }

  const closed = ["EXPIRED", "CANCELLED"].includes(pix.status);
  return (
    <Card className="p-5 mt-6 gold-border" data-testid="pix-card">
      <div className="flex justify-between items-start gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Pagamento PIX</div>
          <div className="font-medium mt-1" data-testid="pix-plan">{pix.plan_label}</div>
        </div>
        <div className="font-mono font-bold text-lg gold-text" data-testid="pix-amount">
          {pix.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
        </div>
      </div>
      {closed ? (
        <div className="mt-4">
          <p className="text-sm text-muted-foreground" data-testid="pix-expired">Este código expirou.</p>
          <Button className="gold-btn mt-3" onClick={initial ? onPaid : create} data-testid="pix-new-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Gerar novo código
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-5 flex justify-center">
            <div className="bg-white p-3 rounded-xl" data-testid="pix-qrcode">
              <QRCodeSVG value={pix.payment_code || ""} size={220} />
            </div>
          </div>
          <div className="mt-4 text-[11px] uppercase tracking-wider text-muted-foreground">PIX copia e cola</div>
          <div className="mt-1 flex gap-2">
            <div className="flex-1 min-w-0 p-2 rounded-lg bg-muted/60 font-mono text-xs break-all max-h-20 overflow-y-auto" data-testid="pix-code">
              {pix.payment_code}
            </div>
            <Button variant="outline" size="sm" onClick={copy} data-testid="pix-copy-btn">
              <Copy className="h-3 w-3" />
            </Button>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground" data-testid="pix-status">
            <Loader2 className="h-3 w-3 animate-spin" /> Aguardando pagamento — {initial ? "o pedido é confirmado" : "a ativação é"} automática após a confirmação.
          </div>
          {pix.expires_at && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              Válido até {new Date(pix.expires_at).toLocaleString("pt-BR")}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
